/**
 * Thin fetch wrapper used by every resource in the api-client.
 *
 * Responsibilities:
 *   - Compose `baseUrl` + path into an absolute URL.
 *   - Attach `Authorization: Bearer <idToken>` when the injected
 *     `getIdToken()` returns one. The API Gateway JWT authorizer validates
 *     the `aud` claim (== Cognito app-client id) which is only present on
 *     Cognito **ID tokens**, so the primary bearer on every authenticated
 *     route is the ID token — not the access token.
 *   - For the handful of endpoints that call Cognito on the caller's
 *     behalf (password change — see docs/api.md §3.2), additionally attach
 *     `X-Cognito-Access-Token: <accessToken>` from the injected
 *     `getAccessToken()`. Cognito `ChangePassword` requires an access token;
 *     the ID token does not work there. See audit finding #3.
 *   - Attach the public-session proof only when a resource supplies the narrow
 *     `sessionToken` request option. Arbitrary caller-supplied headers are not
 *     supported.
 *   - Emit a per-request `X-Request-Id` (UUID v4) so the server can log
 *     it in the same structured field the dashboard/backoffice surface
 *     in error UIs.
 *   - Parse `application/problem+json` error bodies into ApiError.
 *   - Validate 2xx JSON responses against the caller-supplied Zod schema.
 *   - Retry idempotent GETs on network errors + 5xx with exponential
 *     backoff + jitter (task 06 work-item §2.4). Non-idempotent verbs
 *     (POST/PATCH/DELETE) and 4xx responses are never retried.
 */
import { ApiError, problemToApiError } from './errors.js';
/**
 * RFC 4122 v4 UUID. Uses `crypto.randomUUID` when available (Node 18+, all
 * modern browsers) and falls back to a `crypto.getRandomValues`-seeded
 * generator otherwise.
 */
function uuidv4() {
    const c = globalThis.crypto;
    if (c?.randomUUID)
        return c.randomUUID();
    const bytes = new Uint8Array(16);
    c?.getRandomValues?.(bytes);
    // Per RFC 4122 §4.4
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function buildUrl(baseUrl, path, query) {
    const trimmedBase = baseUrl.replace(/\/+$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = `${trimmedBase}${normalizedPath}`;
    if (!query)
        return url;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
        if (v === undefined)
            continue;
        params.append(k, String(v));
    }
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
}
async function parseBody(res) {
    const text = await res.text();
    if (text.length === 0)
        return undefined;
    try {
        return JSON.parse(text);
    }
    catch {
        return text;
    }
}
const DEFAULT_SLEEP = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
});
/**
 * Methods safe to retry. RFC 7231 formally calls GET, HEAD, PUT, DELETE
 * idempotent — but "idempotent at the protocol layer" is not the same as
 * "safe to retry in the face of uncertain server state." Our PUT / DELETE
 * endpoints mutate state (e.g. PUT `/v1/tenant/agents/{id}/instructions/{lang}`
 * overwrites an instruction row and bumps `updatedAt`), so a 5xx retry could
 * surface as the user seeing a "saved twice" signal in the dashboard even
 * though the state converges. We restrict retries to GET only until a concrete
 * need for broader retry emerges; callers who want PUT retries can build that
 * on top of `HttpClient` without changing the default.
 */
const IDEMPOTENT_METHODS = new Set(['GET']);
function isRetriableStatus(status) {
    // 5xx server errors (we exclude 501 Not Implemented — retrying won't help).
    return status >= 500 && status !== 501;
}
export function createHttpClient(options) {
    const fetchImpl = options.fetch ?? globalThis.fetch;
    if (!fetchImpl) {
        throw new Error('api-client: no fetch implementation available. Pass options.fetch or run on a platform with globalThis.fetch.');
    }
    const retryCfg = options.retry ?? {};
    const maxRetries = retryCfg.maxRetries ?? 2;
    const baseDelayMs = retryCfg.baseDelayMs ?? 100;
    const maxDelayMs = retryCfg.maxDelayMs ?? 2000;
    const sleep = retryCfg.sleep ?? DEFAULT_SLEEP;
    const random = retryCfg.random ?? Math.random;
    function backoff(attempt) {
        // Attempt is 1-indexed (1 = first retry). Exponential with full jitter.
        const raw = Math.min(maxDelayMs, baseDelayMs * 2 ** (attempt - 1));
        return Math.floor(random() * raw);
    }
    function abortReason(signal) {
        return signal.reason ?? new DOMException('The operation was aborted.', 'AbortError');
    }
    async function sleepForRetry(ms, signal) {
        if (!signal) {
            await sleep(ms);
            return;
        }
        if (signal.aborted)
            throw abortReason(signal);
        let onAbort;
        const aborted = new Promise((_resolve, reject) => {
            onAbort = () => reject(abortReason(signal));
            signal.addEventListener('abort', onAbort, { once: true });
        });
        try {
            await Promise.race([sleep(ms), aborted]);
        }
        finally {
            if (onAbort)
                signal.removeEventListener('abort', onAbort);
        }
    }
    return {
        async request(opts) {
            const url = buildUrl(options.baseUrl, opts.path, opts.query);
            const retriable = IDEMPOTENT_METHODS.has(opts.method) && maxRetries > 0;
            const maxAttempts = retriable ? maxRetries + 1 : 1;
            let lastNetworkError;
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                if (opts.signal?.aborted)
                    throw abortReason(opts.signal);
                // Fresh UUID per _transmission_ — helps correlate retries in server logs.
                const requestId = uuidv4();
                const headers = {
                    Accept: 'application/json, application/problem+json',
                    'X-Request-Id': requestId,
                };
                if (opts.body !== undefined) {
                    headers['Content-Type'] = 'application/json';
                }
                if (!opts.anonymous && options.getIdToken) {
                    const idToken = await options.getIdToken();
                    if (idToken) {
                        headers.Authorization = `Bearer ${idToken}`;
                    }
                }
                if (!opts.anonymous && opts.needsAccessToken && options.getAccessToken) {
                    const accessToken = await options.getAccessToken();
                    if (accessToken) {
                        headers['X-Cognito-Access-Token'] = accessToken;
                    }
                }
                if (opts.sessionToken !== undefined) {
                    headers['X-CloneAI-Session-Token'] = opts.sessionToken;
                }
                const init = {
                    method: opts.method,
                    headers,
                    ...(opts.signal ? { signal: opts.signal } : {}),
                };
                if (opts.body !== undefined) {
                    init.body = JSON.stringify(opts.body);
                }
                let res;
                try {
                    res = await fetchImpl(url, init);
                }
                catch (err) {
                    if (opts.signal?.aborted)
                        throw abortReason(opts.signal);
                    // Network error — retry if we have budget and the verb is idempotent.
                    lastNetworkError = err;
                    if (attempt < maxAttempts) {
                        await sleepForRetry(backoff(attempt), opts.signal);
                        continue;
                    }
                    const networkError = new ApiError({
                        status: 0,
                        title: 'Network request failed',
                        errorCode: 'NETWORK_ERROR',
                        requestId,
                    });
                    options.onApiError?.(networkError, { requestId, traceId: null });
                    throw err;
                }
                const responseRequestId = res.headers.get('x-request-id') ?? requestId;
                // X-Ray propagates the full trace header on the response. We capture
                // it verbatim so Sentry tags + breadcrumbs can deep-link back into
                // ServiceLens (see docs/runbooks/observability.md).
                const traceId = res.headers.get('x-amzn-trace-id');
                if (!res.ok) {
                    // Retry on 5xx for idempotent verbs only.
                    if (attempt < maxAttempts && isRetriableStatus(res.status)) {
                        // Body must be drained before we discard the response.
                        await res.text().catch(() => undefined);
                        await sleepForRetry(backoff(attempt), opts.signal);
                        continue;
                    }
                    const raw = await parseBody(res);
                    const apiError = problemToApiError(res.status, raw, { requestId: responseRequestId });
                    // Breadcrumb first so the failing request is present on the scope
                    // by the time the capture runs — matches what Sentry's default
                    // http integration does on the server.
                    options.addBreadcrumb?.({
                        method: opts.method,
                        path: opts.path,
                        requestId: responseRequestId,
                        traceId,
                        status: res.status,
                        ok: false,
                    });
                    if (apiError.status >= 500) {
                        options.onApiError?.(apiError, { requestId: responseRequestId, traceId });
                    }
                    throw apiError;
                }
                options.addBreadcrumb?.({
                    method: opts.method,
                    path: opts.path,
                    requestId: responseRequestId,
                    traceId,
                    status: res.status,
                    ok: true,
                });
                const raw = await parseBody(res);
                // 204 No Content + schemas that allow undefined/void: pass through.
                const parsed = opts.responseSchema.safeParse(raw);
                if (!parsed.success) {
                    // Schema drift between client and server — surface as ApiError so the
                    // caller's existing error path handles it (dashboards show a toast with
                    // a request-id). `errorCode` is synthetic; status is the real 200/204.
                    const apiError = new ApiError({
                        status: res.status,
                        title: 'Response failed client-side schema validation',
                        detail: parsed.error.message,
                        errorCode: 'CLIENT_SCHEMA_MISMATCH',
                        requestId: responseRequestId,
                    });
                    options.onApiError?.(apiError, { requestId: responseRequestId, traceId });
                    throw apiError;
                }
                return parsed.data;
            }
            // Unreachable under normal control flow; the loop always returns or throws.
            // This line exists only to satisfy the compiler on paths where the final
            // attempt threw a network error that was caught + swallowed.
            throw lastNetworkError ?? new Error('api-client: request exhausted retries');
        },
    };
}
//# sourceMappingURL=http.js.map